/**
 * Shared building blocks of the inspector sections (W2-06; DESIGN.md §4.11):
 *  - `useSectionBase`: the store, tempo, level, units preference and beat display every section needs;
 *  - `Row`: the focus-session wrapper of one field (coalescing per focus, tx.ts);
 *  - `WithInfo` / `InfoTip`: an `{info}` popover after a control (one sentence + RPE / community term);
 *  - `Callout`: an explanation line with an icon (colour is never the only signal);
 *  - small two-frame diagrams for "Also turn with parent" and "Hide notes under the line";
 *  - number / beat formatting for readouts.
 */

import { useCallback, useMemo, type ReactNode } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import type { Beat, BeatParseContext, Sec, TempoMap } from '../../core/time/types';
import type { ChartDoc, Marker } from '../../core/model/types';
import { Units } from '../../core/model/units';
import { formatBeat, formatSec } from '../../core/time/beatSyntax';
import { useT } from '../../i18n/useT';
import type { MessageKey } from '../../i18n/types';
import type { DocStore, ExperienceLevel } from '../../state/types';
import { Icon, InfoPopover } from '../../ui';
import { useDocValue, useEditorValue, useServicesStore, useSettings, useTempo } from './hooks';
import { m } from './inspector.messages';
import s from './Inspector.module.css';
import type { EditKit } from './tx';

// ---------------------------------------------------------------------------------------------------------------
// Section base
// ---------------------------------------------------------------------------------------------------------------

export interface SectionBase {
  readonly store: DocStore;
  readonly tempo: TempoMap;
  readonly level: ExperienceLevel;
  readonly settings: UserSettings;
  /** Units preference (Full: friendly or RPE raw; Guided: always friendly). */
  readonly rawUnits: boolean;
  readonly fmtBeat: (b: Beat) => string;
}

/** The open store (null outside a project) and the shared view settings. */
export function useStore(services: AppServices): DocStore | null {
  return useServicesStore(services);
}

/** Shared values of a section bound to a store. */
export function useSectionBase(services: AppServices, store: DocStore): SectionBase {
  const settings = useSettings(services);
  const tempo = useTempo(store);
  const level = settings.general.level;
  const style = settings.editing.beatDisplay;
  return {
    store,
    tempo,
    level,
    settings,
    rawUnits: level === 'full' && settings.editing.units === 'rpe',
    fmtBeat: (b) => formatBeat(b, style),
  };
}

function markersOf(doc: ChartDoc): ReadonlyMap<number, Marker> {
  return doc.markers;
}

/** Beat parse context for beat fields: snap divisor, beat syntax setting, tempo and marker names (`m12+2`). */
export function useBeatCtx(services: AppServices, base: SectionBase): BeatParseContext {
  const snapDiv = useEditorValue(services, (st) => st.snap.div);
  const markers = useDocValue(base.store, markersOf, ['markers']);
  const syntax = base.settings.editing.beatSyntax;
  const tempo = base.tempo;
  const marker = useCallback(
    (name: string): Beat | undefined => {
      const wanted = name.trim().toLowerCase();
      for (const mk of markers.values()) if (mk.name.trim().toLowerCase() === wanted) return mk.t;
      return undefined;
    },
    [markers],
  );
  return useMemo(
    () => ({ snapDiv: Math.max(1, Math.min(1920, Math.round(snapDiv))), syntax, tempo, marker }),
    [snapDiv, syntax, tempo, marker],
  );
}

/** Units toggle for a number field (Full level): flips the global units preference. */
export function unitToggleFor(
  services: AppServices,
  base: SectionBase,
  friendly: string,
  raw: string,
):
  { readonly raw: string; readonly friendly: string; readonly mode: 'raw' | 'friendly'; onToggle(): void } | undefined {
  if (base.level !== 'full' || friendly === raw) return undefined;
  return {
    raw,
    friendly,
    mode: base.rawUnits ? 'raw' : 'friendly',
    onToggle: () => {
      services.settings.set('editing', 'units', base.rawUnits ? 'friendly' : 'rpe');
    },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------------------------------------------

/** Joins class names, skipping falsy parts. */
export function cx(...parts: ReadonlyArray<string | false | null | undefined>): string {
  return parts.filter((p): p is string => typeof p === 'string' && p !== '').join(' ');
}

/**
 * One field row with its own focus session (edits coalesce while focus stays inside, D§4.11). `data-field-row`
 * lets `inspector.focusField` reach the row's actions when the field's own control is disabled.
 */
export function Row(props: {
  readonly edit: EditKit;
  readonly field: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}) {
  return (
    <div className={cx(s.row, props.className)} data-field-row={props.field} {...props.edit.row(props.field)}>
      {props.children}
    </div>
  );
}

export interface InfoSpec {
  readonly text: MessageKey;
  readonly rpe?: string;
  readonly community?: string;
  readonly illustration?: string;
}

/** `{info}` popover: one sentence, the RPE / community term and an optional looping illustration. */
export function InfoTip(props: InfoSpec) {
  return (
    <InfoPopover
      text={{ key: props.text }}
      {...(props.rpe === undefined ? {} : { rpeTerm: props.rpe })}
      {...(props.community === undefined ? {} : { communityTerm: props.community })}
      {...(props.illustration === undefined ? {} : { illustration: props.illustration })}
    />
  );
}

/** A control followed by its info popover. */
export function WithInfo(props: { readonly info: InfoSpec; readonly children: ReactNode }) {
  return (
    <div className={s.withInfo}>
      <div className={s.grow}>{props.children}</div>
      <InfoTip {...props.info} />
    </div>
  );
}

export type CalloutTone = 'info' | 'warning' | 'danger' | 'success';

const TONE_ICON: Readonly<Record<CalloutTone, string>> = {
  info: 'Info',
  warning: 'TriangleAlert',
  danger: 'OctagonAlert',
  success: 'CircleCheck',
};

/** An explanation with an icon and optional actions. */
export function Callout(props: {
  readonly tone?: CalloutTone;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
  readonly testId?: string;
}) {
  const tone = props.tone ?? 'info';
  return (
    <div
      className={s.callout}
      data-tone={tone}
      data-testid={props.testId}
      role={tone === 'danger' ? 'alert' : undefined}
    >
      <span className={s.calloutIcon}>
        <Icon name={TONE_ICON[tone]} size={14} />
      </span>
      <span className={s.calloutText}>{props.children}</span>
      {props.actions !== undefined && <span className={s.calloutActions}>{props.actions}</span>}
    </div>
  );
}

/** A muted one-line readout under a field. */
export function Readout(props: { readonly children: ReactNode; readonly testId?: string }) {
  return (
    <p className={s.readout} data-testid={props.testId}>
      {props.children}
    </p>
  );
}

/** A wrapping row of buttons. */
export function Actions(props: { readonly children: ReactNode; readonly label?: string }) {
  return (
    <div className={s.actions} role="group" aria-label={props.label}>
      {props.children}
    </div>
  );
}

/** Section title with an optional muted detail ("14 notes" + "9 Tap, 3 Drag"). */
export function Title(props: { readonly main: ReactNode; readonly detail?: ReactNode }) {
  return (
    <span className={s.title}>
      <span>{props.main}</span>
      {props.detail !== undefined && props.detail !== null && props.detail !== '' && (
        <span className={s.titleDetail}>{props.detail}</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Diagrams (design-token colours through CSS classes)
// ---------------------------------------------------------------------------------------------------------------

const DW = 120;
const DH = 72;

function rot(x: number, y: number, cx0: number, cy0: number, deg: number): readonly [number, number] {
  const r = (deg * Math.PI) / 180;
  const dx = x - cx0;
  const dy = y - cy0;
  return [cx0 + dx * Math.cos(r) - dy * Math.sin(r), cy0 + dx * Math.sin(r) + dy * Math.cos(r)];
}

/** One frame: parent line through the pivot, the child at an offset with its own short line. */
function ParentFrame(props: { readonly parentDeg: number; readonly childTurns: boolean; readonly caption: string }) {
  const px = 44;
  const py = 40;
  const [ax, ay] = rot(px - 34, py, px, py, props.parentDeg);
  const [bx, by] = rot(px + 34, py, px, py, props.parentDeg);
  const [cx1, cy1] = rot(px + 40, py - 22, px, py, props.parentDeg);
  const childDeg = props.childTurns ? props.parentDeg : 0;
  const [c0x, c0y] = rot(cx1 - 16, cy1, cx1, cy1, childDeg);
  const [c1x, c1y] = rot(cx1 + 16, cy1, cx1, cy1, childDeg);
  return (
    <figure className={s.diagramFrame}>
      <svg viewBox={`0 0 ${String(DW)} ${String(DH)}`} width={DW} height={DH} aria-hidden>
        <line className={s.dParent} x1={ax} y1={ay} x2={bx} y2={by} />
        <circle className={s.dPivot} cx={px} cy={py} r={2.5} />
        <line className={s.dLink} x1={px} y1={py} x2={cx1} y2={cy1} />
        <line className={s.dChild} x1={c0x} y1={c0y} x2={c1x} y2={c1y} />
        <circle className={s.dChildDot} cx={cx1} cy={cy1} r={2} />
      </svg>
      <figcaption>{props.caption}</figcaption>
    </figure>
  );
}

/** Two-frame "Also turn with parent" diagram (D§4.21): the parent at 0 deg, then turned. */
export function RotateWithParentDiagram(props: { readonly on: boolean }) {
  const t = useT();
  return (
    <div className={s.diagram} data-testid="rotate-diagram" data-on={props.on ? 'true' : 'false'}>
      <ParentFrame parentDeg={0} childTurns={props.on} caption={t(m.diagramBefore)} />
      <Icon name="ArrowRight" size={14} />
      <ParentFrame parentDeg={35} childTurns={props.on} caption={t(props.on ? m.diagramTurns : m.diagramKeeps)} />
    </div>
  );
}

/** "Hide notes under the line" diagram: a note behind the line is hidden (dashed) or drawn. */
export function CoverDiagram(props: { readonly on: boolean }) {
  const t = useT();
  return (
    <figure className={s.diagramFrame} data-testid="cover-diagram">
      <svg viewBox={`0 0 ${String(DW)} ${String(DH)}`} width={DW} height={DH} aria-hidden>
        <line className={s.dParent} x1={8} y1={36} x2={112} y2={36} />
        <rect className={s.dNote} x={26} y={12} width={22} height={5} rx={1} />
        <rect className={props.on ? s.dNoteHidden : s.dNote} x={72} y={52} width={22} height={5} rx={1} />
        <line className={s.dArrow} x1={83} y1={50} x2={83} y2={42} />
      </svg>
      <figcaption>{t(props.on ? m.coverDiagramOn : m.coverDiagramOff)}</figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------------------------------------------

/** A number for readouts: at most `decimals` decimals, trailing zeros removed. */
export function fmtNum(v: number, decimals = 3): string {
  if (!Number.isFinite(v)) return String(v);
  const r = Number(v.toFixed(decimals));
  return String(Object.is(r, -0) ? 0 : r);
}

type Translate = (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;

/**
 * "Notes cross the screen in 0.75 s" for scroll speed values (D§5.6.7): one value, or the range of a segment
 * (`to` differs from `from`). A speed of 0 or less never brings notes to the line.
 */
export function crossScreenText(t: Translate, from: number, to: number = from): string {
  const a = Units.crossScreenSec(from);
  const b = Units.crossScreenSec(to);
  if (!Number.isFinite(a) && !Number.isFinite(b)) return t(m.crossScreenNever);
  if (!Number.isFinite(a) || !Number.isFinite(b) || fmtNum(a, 2) === fmtNum(b, 2)) {
    return t(m.crossScreen, { sec: fmtNum(Number.isFinite(a) ? a : b, 2) });
  }
  return t(m.crossScreenRange, { from: fmtNum(a, 2), to: fmtNum(b, 2) });
}

/** Seconds with 3 decimals ("1.250"). */
export function fmtSec3(sec: Sec): string {
  return Number.isFinite(sec) ? sec.toFixed(3) : '-';
}

/** "0:24.512". */
export function fmtClock(sec: Sec): string {
  return formatSec(sec);
}
